import { useEffect, useRef, useState } from "react";

interface StrokedTextProps {
  text?: string;
  strokeWidth?: number;
  strokeColor?: string;
  fillColor?: string;
  fontSize?: number;
  fontFamily?: string;
  className?: string;
  fontWeight?:
    | "normal"
    | "bold"
    | "medium"
    | "semibold"
    | "light"
    | "thin"
    | "hairline";
  children?: string | string[];
  letterSpacing?: number;
  height?: number;
}

/**
 * Renders text with a clean stroke outline using SVG for best visual quality.
 * SVG provides the sharpest stroke rendering compared to CSS approaches.
 * Automatically measures and adjusts to actual text width.
 */
export const StrokedText = ({
  text,
  children,
  strokeWidth = 3,
  strokeColor = "#1d222e",
  fillColor = "#FFFFFF",
  fontSize = 24,
  fontFamily = "vck",
  fontWeight = "normal",
  className = "",
  letterSpacing = 0,
  height,
}: StrokedTextProps) => {
  const textRef = useRef<SVGTextElement>(null);
  const [textWidth, setTextWidth] = useState(0);

  useEffect(() => {
    if (textRef.current) {
      const bbox = textRef.current.getBBox();
      setTextWidth(bbox.width);
    }
  }, [text, children, fontSize, fontFamily]);

  const content = text || children;

  return (
    <svg
      className={className}
      style={{
        overflow: "visible",
        display: "inline-block",
      }}
      height={height ?? fontSize * 1.5}
      width={textWidth || "auto"}
    >
      <text
        ref={textRef}
        x="0"
        y={height ? height / 2 : fontSize}
        textAnchor="start"
        fontSize={fontSize}
        fontFamily={fontFamily}
        fill={fillColor}
        stroke={strokeColor}
        strokeWidth={strokeWidth}
        strokeLinejoin="round"
        strokeLinecap="round"
        paintOrder="stroke fill"
        letterSpacing={letterSpacing}
        fontWeight={fontWeight}
      >
        {content}
      </text>
    </svg>
  );
};
